import { Box, IconButton, Tooltip } from '@mui/material';
import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { Attachment } from 'sr-types/lib/production/v1/graphql';
import { useI18n } from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { V } from '../common/Layout';
import { getAttachmentReferenceType, getReferenceTypeAttribute } from '../common/lexical/attachmentReferenceUtils';
import {
    MATERIAL_FILE_UPLOADED_ATTRIBUTE,
    MATERIAL_REFERENCE_ATTRIBUTE,
    MATERIAL_REFERENCE_CLASS,
    MATERIAL_REFERENCE_ID_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_CLASS,
    MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE,
    MATERIAL_REFERENCE_TYPE_ATTRIBUTE
} from '../common/lexical/lexicalConstants';
import { UUID_REGEX } from './constants';

interface AttachmentReferenceProps {
    attachment: Attachment;
    isEditable: boolean;
    onRemove?: (attachmentId: string) => void;
}

interface MaterialCellProps {
    value?: string;
    isEditable?: boolean;
    onAttachmentRemove?: (attachment: Attachment) => void;
}

const REMOVE_ICON_BUTTON_SX = {
    color: 'inherit',
    height: 16,
    ml: 0.25,
    p: 0,
    width: 16
};

const REMOVE_ICON_SX = { fontSize: 12 };

const AttachmentReference = ({ attachment, isEditable, onRemove }: AttachmentReferenceProps): ReactElement => {
    const referenceId = attachment?.entityReference?.id ? attachment.entityReference.id : attachment.id;
    const label = attachment.name || attachment.entityReference?.label;
    const referenceType = getAttachmentReferenceType(attachment);
    const mimeType = attachment.type;

    const addMimeTypeAttribute = mimeType && mimeType !== 'import';

    // Check if this is an imported attachment flow.
    const isImportedAttachment = mimeType === 'import' && referenceType === 'attachment';

    // Check if the referenceId is a valid UUID format.
    const isValidUuid = UUID_REGEX.test(referenceId.trim());
    const tooltipTitleForMissingFile = useI18n('import.material.missing.file.tooltip');

    const referenceTypeAttribute = getReferenceTypeAttribute(referenceType, referenceId);

    const showTooltip = isImportedAttachment && !isValidUuid;

    const tooltipTitle = showTooltip ? tooltipTitleForMissingFile : '';

    const handleRemoveClick = useCallback(() => onRemove?.(attachment.id), [attachment.id, onRemove]);

    return (
        <Tooltip title={tooltipTitle}>
            <Box
                component="span"
                className={MATERIAL_REFERENCE_CLASS}
                contentEditable={false}
                {...{
                    [MATERIAL_REFERENCE_ATTRIBUTE]: 'true',
                    [MATERIAL_REFERENCE_ID_ATTRIBUTE]: referenceId,
                    [MATERIAL_REFERENCE_TYPE_ATTRIBUTE]: referenceTypeAttribute,
                    [MATERIAL_REFERENCE_LABEL_ATTRIBUTE]: label,
                    ...(addMimeTypeAttribute && {
                        [MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE]: mimeType
                    }),
                    ...(isImportedAttachment && {
                        [MATERIAL_FILE_UPLOADED_ATTRIBUTE]: isValidUuid ? 'true' : 'false'
                    })
                }}
            >
                <Box component="span" className={MATERIAL_REFERENCE_LABEL_CLASS}>
                    {label}
                </Box>

                {isEditable ? (
                    <IconButton
                        size="small"
                        aria-label={`Remove ${label}`}
                        onClick={handleRemoveClick}
                        sx={REMOVE_ICON_BUTTON_SX}
                    >
                        <Icons.Close sx={REMOVE_ICON_SX} />
                    </IconButton>
                ) : null}
            </Box>
        </Tooltip>
    );
};

export default function MaterialCellRenderer({
    value,
    isEditable,
    onAttachmentRemove
}: MaterialCellProps): ReactElement | null {
    const [attachments, setAttachments] = useState<Attachment[]>([]);

    useEffect(() => {
        if (!value?.trim()) {
            setAttachments([]);
            return;
        }

        try {
            const parsedValue: unknown = JSON.parse(value.trim());
            setAttachments(Array.isArray(parsedValue) ? (parsedValue as Attachment[]) : []);
        } catch {
            setAttachments([]);
        }
    }, [value]);

    const handleRemove = useCallback(
        (attachmentId: string) => {
            setAttachments((currentAttachments) => {
                const removedAttachment = currentAttachments.find((attachment) => attachment.id === attachmentId);
                const updatedAttachments = currentAttachments.filter((attachment) => attachment.id !== attachmentId);

                removedAttachment && onAttachmentRemove?.(removedAttachment);

                return updatedAttachments;
            });
        },
        [onAttachmentRemove]
    );

    if (!attachments.length) {
        return null;
    }

    return (
        <V>
            {attachments.map((attachment) => (
                <AttachmentReference
                    key={attachment.id}
                    attachment={attachment}
                    isEditable={isEditable}
                    onRemove={handleRemove}
                />
            ))}
        </V>
    );
}
